import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";

const AUSTRALIA_GEO_URL =
  "https://raw.githubusercontent.com/rowanhogan/australian-states/master/states.geojson";

const stateCoordinates = {
  "New South Wales": [147.2, -32.5],
  Victoria: [144.96, -37.81],
  Queensland: [143.0, -23.5],
  "South Australia": [138.6, -34.9],
  "Western Australia": [121.9, -25.8],
  Tasmania: [147.3, -42.9],
  "Northern Territory": [133.8, -19.3],
  "Australian Capital Territory": [149.1, -35.3],
};

// Helper function to normalize state names for matching
const normalizeStateName = (stateName) => {
  if (!stateName) return "";
  
  // Trim whitespace
  let normalized = stateName.trim();
  
  // Handle common variations and abbreviations
  const stateMapping = {
    "NSW": "New South Wales",
    "New South Wales": "New South Wales",
    "VIC": "Victoria",
    "Victoria": "Victoria",
    "QLD": "Queensland",
    "Queensland": "Queensland",
    "SA": "South Australia",
    "South Australia": "South Australia",
    "WA": "Western Australia",
    "Western Australia": "Western Australia",
    "TAS": "Tasmania",
    "Tasmania": "Tasmania",
    "NT": "Northern Territory",
    "Northern Territory": "Northern Territory",
    "ACT": "Australian Capital Territory",
    "Australian Capital Territory": "Australian Capital Territory"
  };
  
  // Check if we have a direct mapping
  if (stateMapping[normalized]) {
    return stateMapping[normalized];
  }
  
  // Try case-insensitive matching
  const matchingKey = Object.keys(stateMapping).find(key => 
    normalized.toLowerCase() === key.toLowerCase()
  );
  
  return matchingKey ? stateMapping[matchingKey] : normalized;
};

// Helper function to get coordinates with fuzzy matching
const getCoordinatesForState = (stateName) => {
  if (!stateName) return null;
  
  // Try exact match first
  if (stateCoordinates[stateName]) {
    return stateCoordinates[stateName];
  }
  
  // Try normalized match
  const normalized = normalizeStateName(stateName);
  if (stateCoordinates[normalized]) {
    return stateCoordinates[normalized];
  }
  
  // Try case-insensitive matching
  const matchingKey = Object.keys(stateCoordinates).find(key => 
    key.toLowerCase() === stateName.toLowerCase()
  );
  
  return matchingKey ? stateCoordinates[matchingKey] : null;
};

const RegionMap = () => {
  const { redemptionByDemographics } = useSelector(
    (state) => state?.merchantReducer
  );

  const [tooltipContent, setTooltipContent] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipDimensions, setTooltipDimensions] = useState({ width: 200, height: 200 });

  const mapContainerRef = useRef(null);
  const tooltipRef = useRef(null);
  const mapRef = useRef(null);

  // Measure tooltip dimensions after it renders
  useEffect(() => {
    if (tooltipRef.current && tooltipContent) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipDimensions({
        width: rect.width,
        height: rect.height
      });
    }
  }, [tooltipContent]);

  // Adjust tooltip position to stay within boundaries
  useEffect(() => {
    if (tooltipContent && mapContainerRef.current) {
      const mapRect = mapContainerRef.current.getBoundingClientRect();
      
      // Get the actual map dimensions, not just the container
      const mapWidth = mapRef.current ? mapRef.current.getBoundingClientRect().width : mapRect.width;
      const mapHeight = mapRef.current ? mapRef.current.getBoundingClientRect().height : mapRect.height;
      
      // Current position
      let { x, y } = tooltipPosition;
      
      // Adjust x to keep tooltip within horizontal bounds
      if (x + tooltipDimensions.width > mapWidth) {
        x = Math.max(0, mapWidth - tooltipDimensions.width - 10);
      }
      
      // Adjust y to keep tooltip within vertical bounds
      if (y + tooltipDimensions.height > mapHeight) {
        y = Math.max(0, mapHeight - tooltipDimensions.height - 10);
      }

      // Only update if position changed
      if (x !== tooltipPosition.x || y !== tooltipPosition.y) {
        setTooltipPosition({ x, y });
      }
    }
  }, [tooltipContent, tooltipPosition, tooltipDimensions]);

  const handleMarkerClick = (marker, event) => {
    // Toggle tooltip if same marker clicked
    if (activeMarker === marker.state) {
      setTooltipContent(null);
      setActiveMarker(null);
      return;
    }

    // Handle different data structures for ageGroup and gender
    let ageGroups = [];
    let genders = [];
    
    // Check if ageGroup is an object or array
    if (marker.ageGroup && typeof marker.ageGroup === 'object') {
      if (Array.isArray(marker.ageGroup)) {
        // If it's already an array of [key, value] pairs
        ageGroups = marker.ageGroup;
      } else {
        // If it's an object, convert to array of [key, value] pairs
        ageGroups = Object.entries(marker.ageGroup);
      }
    }
    
    // Check if gender is an object or array
    if (marker.gender && typeof marker.gender === 'object') {
      if (Array.isArray(marker.gender)) {
        // If it's already an array of [key, value] pairs
        genders = marker.gender;
      } else {
        // If it's an object, convert to array of [key, value] pairs
        genders = Object.entries(marker.gender);
      }
    }

    const content = (
      <div className="p-2 text-sm">
        <h3 className="font-bold">{marker.state}</h3>
        <div className="mt-2">
          <span className="font-semibold">Age Groups:</span>
          {ageGroups.length > 0 ? (
            <ul className="list-disc pl-5">
              {ageGroups.map(([group, count]) => (
                <li key={group}>
                  {group}: {count}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 ml-5">No age group data</p>
          )}
        </div>
        <div className="mt-2">
          <span className="font-semibold">Gender Distribution:</span>
          {genders.length > 0 ? (
            <ul className="list-disc pl-5">
              {genders.map(([group, count]) => (
                <li key={group}>
                  {group}: {count}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 ml-5">No gender data</p>
          )}
        </div>
      </div>
    );

    // Get map container dimensions
    const mapContainer = mapContainerRef.current?.getBoundingClientRect();
    if (!mapContainer) return;

    // Calculate initial position near the marker
    const mapWidth = mapRef.current ? mapRef.current.getBoundingClientRect().width : mapContainer.width;
    
    // Start with preferred position (right of marker)
    let newX = event.nativeEvent.offsetX + 15;
    let newY = event.nativeEvent.offsetY - 10;
    
    // If we're in the right third of the map, position tooltip to the left of marker
    if (event.nativeEvent.offsetX > mapWidth * 0.7) {
      newX = event.nativeEvent.offsetX - 215; // Tooltip width (200px) + padding
    }
    
    // If we're in the bottom third of the map, position tooltip above marker
    if (event.nativeEvent.offsetY > mapContainer.height * 0.7) {
      newY = event.nativeEvent.offsetY - 150; // Approximate tooltip height
    }
    
    // Set tooltip content and position
    setTooltipContent(content);
    setActiveMarker(marker.state);
    setTooltipPosition({ x: newX, y: newY });
  };

  // Window resize handler to reposition tooltip
  useEffect(() => {
    const handleResize = () => {
      if (tooltipContent && activeMarker) {
        // Force recalculation of tooltip position
        setTooltipPosition(prev => ({ ...prev }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tooltipContent, activeMarker]);

  // Check if we have data to display
  const hasData = redemptionByDemographics?.data && redemptionByDemographics.data.length > 0;

  return (
    <div
      ref={mapContainerRef}
      className="relative rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart"
    >
      <div className="flex flex-col sm:flex-col xl:flex-row justify-between w-full mt-4">
        <span className="text-[20px] font-poppins font-[600] text-[black] whitespace-nowrap">
          User Demographics by Redemption Location
        </span>
      </div>

      <div 
        ref={mapRef}
        style={{ width: "100%", height: "100%", position: "relative" }}
        // Prevent selection/drag rectangle on mouse interactions
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 1000,
            center: [133.7751, -25.2744],
          }}
        >
          <Geographies geography={AUSTRALIA_GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#e0e0e0"
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { fill: "#e0e0e0", outline: "none" },
                    hover: { fill: "#00ADEF", outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {redemptionByDemographics?.data?.map((marker) => {
            // Use the improved matching function
            const coordinates = getCoordinatesForState(marker.state);
            if (!coordinates) {
              // If we can't find coordinates, skip this marker
              return null;
            }

            return (
              <Marker
                key={marker.state}
                coordinates={coordinates}
                onClick={(event) => handleMarkerClick(marker, event)}
              >
                <circle
                  r={6}
                  fill={activeMarker === marker.state ? "#5A67D8" : "#3C50E0"}
                  stroke="#fff"
                  strokeWidth={2}
                  style={{ cursor: "pointer" }}
                />
              </Marker>
            );
          })}
        </ComposableMap>

        {tooltipContent && (
          <div
            ref={tooltipRef}
            className="absolute bg-white p-1 rounded-xl shadow-lg border border-gray-200 z-10"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              width: "200px", 
              maxWidth: "90%",
              transition: "left 0.2s, top 0.2s"
            }}
          >
            {tooltipContent}
          </div>
        )}

        {/* Show a message if no data is available */}
        {hasData === false && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-gray-500">No demographics data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionMap;